import { useRoutineAlerts, useWorkspaceAsset } from '@gripp/shared-logic';
import { AlertIcon, SlideAction } from '@gripp/shared-ui';
import { useSlideLayoutSafe } from 'libs/shared-ui/src/components/slide-layout/SlideLayout';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAssetRouting } from '../assetRouteContext';

const toRouteStateObject = (state: unknown): Record<string, unknown> =>
  state && typeof state === 'object' && !Array.isArray(state)
    ? (state as Record<string, unknown>)
    : {};

export const AssetRoutineSubmitHeader = ({
  assetId,
  routineId,
}: {
  assetId: string;
  routineId: string;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const slideLayout = useSlideLayoutSafe();
  const { basePath } = useAssetRouting(assetId);
  const { workspaceAsset } = useWorkspaceAsset(assetId);
  const { routineWorkflows, isLoading: areAlertsLoading } = useRoutineAlerts(
    workspaceAsset?.id ?? '',
    routineId,
    undefined,
    { enabled: !!workspaceAsset?.id }
  );

  const onEditButtonPress = () => {
    if (location.pathname.includes('/alerts')) {
      return;
    }

    const routeState = toRouteStateObject(location.state);
    const fromSearchParam = new URLSearchParams(location.search).get('from');
    const preservedState = {
      ...routeState,
      ...(fromSearchParam ? { from: fromSearchParam } : {}),
    };
    const transitionState = slideLayout
      ? { slideLayoutInitialTransition: SlideAction.NEXT }
      : {};

    slideLayout?.prepareSlideTransition(SlideAction.NEXT);

    // Mirrors mobile (apps/gripp-app/src/app/(auth)/routine/index.tsx:171-183):
    // when the routine has no alerts yet, jump straight to the new-alert form
    // instead of showing an empty list. `fromRoutine` lets the form page render
    // the routine submit on the left column (since the alerts list — the usual
    // left panel — would be empty and not the actual previous step).
    //
    // While alerts haven't loaded (workspaceAsset id pending or hook still
    // fetching) we fall back to the alerts list — a safe destination with its
    // own loading state — instead of risking a wrong /alerts/new + fromRoutine
    // jump on a fast click. Only when we know the list is truly empty do we
    // shortcut to the new-alert form.
    const isReady = !!workspaceAsset?.id && !areAlertsLoading;
    const hasAlerts = (routineWorkflows?.length ?? 0) > 0;
    const goToNewAlert = isReady && !hasAlerts;
    const target = goToNewAlert
      ? `${basePath}/routines/${routineId}/alerts/new`
      : `${basePath}/routines/${routineId}/alerts`;

    navigate(target, {
      state: {
        ...preservedState,
        fromRoutine: goToNewAlert,
        ...transitionState,
      },
    });
  };

  return (
    <View style={styles.filtersContainer}>
      <Pressable
        onPress={onEditButtonPress}
        accessibilityRole="button"
        accessibilityLabel={t('alerts.setAlert')}
      >
        <AlertIcon size={24} />
      </Pressable>
    </View>
  );
};

const styles = StyleSheet.create({
  filtersContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 16,
  },
  text: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '600',
  },
});
